import { ClinicAccess, DEFAULT_ACCESS, actingAsSelf, can, canInContext, canManageRoles, canSeeMoney } from './clinicAccessRules';

// WST-319 §9 portal gates. UX only: the backend enforces every rule. Outside a centralized clinic, and acting as
// self, each gate returns the site's existing check (`todayGate`) unchanged.

type GateUser = {
  provider?: {
    uuid?: string | number | null;
    is_admin?: boolean;
    payment_details_enabled?: boolean;
    parent_org?: { allow_provider_session_credit?: boolean; allow_provider_balance_credit?: boolean } | null;
  } | null;
  clinic?: { is_admin?: boolean } | null;
  clinic_access?: ClinicAccess;
} | null | undefined;
type GateProvider = { uuid?: string | number | null; is_admin?: boolean; clinic?: { is_admin?: boolean } | null } | null | undefined;
type GateClinic = { is_admin?: boolean; superbill_allowed?: boolean; parent_org?: { superbill_allowed?: boolean } | null } | null | undefined;

export const accessOf = (currentUser: GateUser): ClinicAccess => currentUser?.clinic_access ?? DEFAULT_ACCESS;

// Role keys apply only in clinic context: a centralized clinic, acting under the override (not as self). A member
// with no role row, and a role holder acting as self, keep today's gates.
export const inRoleContext = (currentUser: GateUser, currentProvider: GateProvider): boolean =>
  accessOf(currentUser).centralized && !actingAsSelf(currentUser ?? undefined, currentProvider ?? undefined);

export const roleGates = (currentUser: GateUser, currentProvider: GateProvider, currentProviderUpdating = false) => {
  const access = accessOf(currentUser);
  const central = access.centralized;
  const roleCtx = inRoleContext(currentUser, currentProvider);
  const money = canSeeMoney(currentUser ?? undefined);
  const ctx = (key: string, todayGate: boolean) =>
    canInContext(access, key, currentUser ?? undefined, currentProvider ?? undefined, todayGate);
  const editClient = ctx('clients.edit', true);
  // admin-only money/manage sites ("is_admin", often with is_org): also a role holder with the key
  const adminOr = (key: string) => !!currentUser?.provider?.is_admin || (roleCtx && can(access, key));
  const org = currentUser?.provider?.parent_org;
  // WST-1159: outside role context the Clinic pages are for clinic admins and solo providers (their create-clinic
  // entry); a clinic member without admin rights has nothing to manage there.
  const clinicAdminOrSolo = !currentUser?.clinic || !!currentUser.clinic.is_admin;
  return {
    access,
    money,
    clinicStaff: roleCtx ? canManageRoles(access) : clinicAdminOrSolo,
    clinicPage: roleCtx
      ? canManageRoles(access) || ['clinic.profile', 'clinic.templates', 'clinic.superbill'].some((k) => can(access, k))
      : clinicAdminOrSolo,
    reports: roleCtx ? access.permissions.some((k) => k.startsWith('reports.')) : money,
    report: (key: string) => ctx(key, true),
    adminOr,
    // Credit radios (session cancel dialog, block-time dialog): admin or billing.credits in context, else the
    // clinic's allow_provider_*_credit flag, as the backend checks under an override. The session flag defaults
    // on, so an unloaded org keeps today's Session radio. currentProvider.is_admin (clinic target) and its
    // clinic.is_admin (member target) cover a Super Admin with no own provider row acting via the switcher (the
    // backend's honored-override exit).
    credit: (kind: 'session' | 'balance') =>
      adminOr('billing.credits') ||
      !!currentProvider?.is_admin ||
      !!currentProvider?.clinic?.is_admin ||
      (kind === 'balance' ? !!org?.allow_provider_balance_credit : org?.allow_provider_session_credit !== false),
    bookingApp: ctx('sessions.book', money),
    book: ctx('sessions.book', true),
    status: ctx('sessions.status', true),
    availability: ctx('sessions.availability', true),
    createClient: ctx('clients.create', true),
    editClient,
    billingDetails: editClient || ctx('billing.details', true),
    notes: ctx('clinical.notes', true),
    documents: ctx('clinical.documents', true),
    records: ctx('clinical.records', true),
    billingTab: ctx('billing.view', money),
    // Skip while switching provider in a centralized clinic: the override header changes before currentProvider does.
    paymentStatus: !(central && currentProviderUpdating) && ctx('billing.view', true),
    oneOff: ctx('billing.oneoff', true),
    plans: ctx('billing.plans', true),
    archive: ctx('clients.archive', true),
    templates: ctx('clinic.templates', true),
    stripe: ctx('clinic.stripe', true),
    // Spec §6 ADMIN rule (no catalog key): CSV export and imports are owner/Super Admin only in role context.
    adminOnly: !roleCtx || canManageRoles(access),
    services: (todayGate: boolean) => todayGate && (!roleCtx || can(access, 'clinic.services')),
  };
};

// Switcher (ClinicTitleBar): shown for clinic_view, the clinic entry only for clinic-wide reach; a Supervisor
// without it lists themself and their supervisees.
export const switcherGate = (access: ClinicAccess, clinic: GateClinic) => ({
  show: access.centralized ? access.clinic_view : !!clinic?.is_admin,
  clinicEntry: !access.centralized || access.clinic_wide,
  onlyProviders: access.centralized && !access.clinic_wide ? access.supervises.providers : undefined,
});

export type ClinicTab = 'profile' | 'staff' | 'settings' | 'tags' | 'locations' | 'note-templates' | 'integrations' | 'superbill';

// Clinic page tabs. Today: every tab for clinic admins, superbill only in superbill clinics. A Clinic Manager
// (in role context, not admin) gets the tabs of their clinic.* keys; no Staff, Settings or Apps.
export const clinicTabs = (access: ClinicAccess, clinic: GateClinic, roleCtx: boolean): ClinicTab[] => {
  const superbillClinic = !!(clinic?.superbill_allowed || clinic?.parent_org?.superbill_allowed);
  if (clinic?.is_admin) {
    const all: ClinicTab[] = ['profile', 'staff', 'settings', 'tags', 'locations', 'note-templates', 'integrations'];
    return superbillClinic ? [...all, 'superbill'] : all;
  }
  if (!roleCtx) return [];
  const tabs: ClinicTab[] = [];
  if (can(access, 'clinic.profile')) tabs.push('profile', 'tags', 'locations');
  if (can(access, 'clinic.templates')) tabs.push('note-templates');
  if (superbillClinic && can(access, 'clinic.superbill')) tabs.push('superbill');
  return tabs;
};

// Clinic Info: owner/Super Admin edit everything; clinic.profile edits profile fields only (EIN, NPI, billing email
// and logo read-only).
export const clinicInfoGate = (access: ClinicAccess, clinic: GateClinic, roleCtx: boolean) => {
  const admin = !!clinic?.is_admin;
  return { canEdit: admin || (roleCtx && can(access, 'clinic.profile')), billingIdentity: admin || !roleCtx };
};
